import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Format-keyed DataSource Writer for CSV. Loads a staged CSV file into the
 * DataSource Link's ingest table, applying the Link's parse config (delimiter,
 * quote, encoding + column mapping) in a single pass. The format/mapping comes
 * from the Link's `parse_config` (which must be `csv`), not from the socket —
 * pair this with the connection-keyed adapter that staged the file.
 */
export const csvDataSourceWriterStep = defineStepConfig({
  type: "csv_datasource_writer",
  name: "Csv DataSource Writer",
  description:
    "Load a staged CSV file into a DataSource Link's ingest table, applying the Link's CSV parse config (mapping + options).",
  category: "DataSource Writer",

  experimental: {
    reason:
      "Csv DataSource Writer is under active development and not yet validated for production workflows.",
  },

  inputSchema: z.object({
    blobStorageUri: z.string().describe("URI of the source CSV file to load"),
    dataSourceLink: z
      .string()
      .uuid()
      .describe(
        "DataSource Link identifying the source connection + credential, the CSV parse config, and the target schema + table",
      ),
    resumeRowOffset: z
      .number()
      .int()
      .nonnegative()
      .optional()
      .describe(
        "Manually resume from this row instead of row 0 — paste the `resumeRowOffset` from a failed run's last heartbeat details when starting a fresh rerun after all retries were exhausted. Ignored when this attempt already has its own heartbeat checkpoint (same-execution automatic retry).",
      ),
  }),

  outputSchema: z.object({
    rowCount: z.number().describe("Rows successfully upserted"),
    failedCount: z
      .number()
      .describe("Rows that failed and were dead-lettered to .failed.csv"),
  }),

  configSchema: z.object({
    batchSize: z
      .number()
      .int()
      .positive()
      .prefault(500)
      .describe("Number of rows per SQL insert batch"),
  }),

  emits: [] as const,

  // Hard-coded fanout zip (size 1 → one file per run): the connection-keyed
  // adapter emits index-aligned blobStorageUris + dataSourceLinks arrays into
  // this writer's two single-value sockets. `batch` makes the engine lockstep-zip
  // them (one URI + its own link per run) instead of cross-producing. Fixed on
  // the step type, so it's not an editable config field and never shows in the UI.
  batch: { size: 1 },

  execution: {
    // Generous ceiling per attempt: a legitimate load can run long (multi-million
    // row files, ingest-pool contention from parallel writers, dirty files that
    // degrade to bisected retries). Liveness is enforced by the 2-minute
    // heartbeatTimeout + resume checkpoint, not this cap.
    startToCloseTimeout: "4 hours",
    // Deploys restart workers frequently, and each restart looks like a dead
    // activity to Temporal (heartbeat timeout) — a routine event, not a
    // processing failure. A high ceiling lets a long file load survive many
    // redeploys across its run; each retry resumes from the last committed
    // checkpoint instead of restarting at row 0, so extra attempts are cheap.
    // `maximumInterval` caps backoff growth so a retry after a worker restart
    // reschedules quickly rather than waiting an ever-longer interval.
    retry: {
      maximumAttempts: 8,
      backoffCoefficient: 2,
      initialInterval: "10 seconds",
      maximumInterval: "1 minute",
    },
  },
});
